<script setup>
import {computed} from "vue";
import {useBookingStore} from "@/stores/booking";
import {useApartmentStore} from "@/stores/apartment";
import AppIcon from "@/components/AppIcon.vue";

const bookingStore = useBookingStore()
const apartmentStore = useApartmentStore()

const location = computed(() => apartmentStore.apartment.location ?? {})

// The API is not guaranteed to deliver a postal address, so fall back to the
// position inside the building, which is always present.
const addressLines = computed(() => {
  const {street, address, postalCode, city} = location.value
  const line1 = street ?? address
  const line2 = [postalCode, city].filter(Boolean).join(' ')

  return [line1, line2].filter(Boolean)
})
</script>

<template>
  <section class="ww-card ww-card--navy stay-card">
    <div class="ww-eyebrow ww-eyebrow--onNavy">{{ $t('home.stayLabel') }}</div>
    <h1 class="stay-card__name">Apartment {{ apartmentStore.apartment.name }}</h1>

    <div class="stay-card__location">
      <app-icon name="location_on" :size="19" class="stay-card__pin"/>
      <div v-if="addressLines.length">
        <template v-for="(line, i) in addressLines" :key="'addr-' + i">
          {{ line }}<br v-if="i < addressLines.length - 1"/>
        </template>
      </div>
      <div v-else-if="location.floor !== undefined">
        {{ location.floor }}. {{ $t('floor') }}, {{ $t(location.side) }}
      </div>
    </div>

    <dl class="stay-card__facts">
      <div>
        <dt>{{ $t('home.arrival') }}</dt>
        <dd>{{ bookingStore.summary.checkIn }}</dd>
      </div>
      <div>
        <dt>{{ $t('home.departure') }}</dt>
        <dd>{{ bookingStore.summary.checkOut }}</dd>
      </div>
      <div>
        <dt>{{ $t('guests', 2) }}</dt>
        <dd>{{ bookingStore.summary.guests }}</dd>
      </div>
    </dl>
  </section>
</template>

<style scoped>
.stay-card {
  padding: 24px 22px 20px;
}

.stay-card__name {
  margin-top: 8px;
  font-size: 30px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.02em;
}

.stay-card__location {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin-top: 10px;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.3;
  color: var(--ww-on-navy);
}

.stay-card__pin {
  color: var(--ww-primary-bright);
  line-height: 1.25;
}

.stay-card__facts {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 14px;
  margin: 22px 0 0;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.14);
}

.stay-card__facts dt {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ww-on-navy-muted);
}

.stay-card__facts dd {
  margin: 5px 0 0;
  font-size: 16px;
  font-weight: 700;
}
</style>
